import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useListingActions } from '../../../hooks/useListingAdmin';
import { useListing } from '../../../hooks/useListings';
import { useShiurWeek } from '../../../hooks/useShiurim';
import { HoursDayType, ShiurSession, TimeRule } from '../../../types';
import { MYZMANIM_FIELDS, SHABBOS_ENDS_MINUTES_AFTER_SUNSET } from '../../../config/constants';
import { LISTING_LIMITS, newId } from '../../../utils/listingEdit';
import { DAYS_OF_WEEK, dayRuns, HOURS_SEASON_MODES, HoursSeasonMode, sortHoursEntries } from '../../../utils/hoursForm';
import {
  checkShiurDraft,
  draftToSession,
  emptyShiurDraft,
  SHIUR_ANCHOR_PICKS,
  SHIUR_ANCHOR_PRAYERS,
  SHIUR_ANCHORS,
  SHIUR_SPECIAL_DAY_TYPES,
  ShiurSessionDraft,
  sessionToDraft,
} from '../../../utils/shiurForm';
import { pickLang } from '../../../utils/listingDisplay';
import { SHABBOS_ENDS_ZMAN } from '../../../utils/openingHours';
import { CANDLE_LIGHTING_ZMAN } from '../../../utils/timeUtils';
import ShiurWeek from '../../directory/ShiurWeek';
import TimeRuleFields from '../TimeRuleFields';
import { BilingualField, Checkbox, ChipSelect, ErrorList, SectionTitle, SelectField, TextField } from './formFields';
import type { TabProps } from './ListingEditor';

/** Max times per shiur (the rules allow 40). */
const MAX_SESSIONS = 40;

type Editing = { id: string | null; draft: ShiurSessionDraft } | null;

export default function ShiurTimesTab({ listing }: TabProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const { updateListing, saving } = useListingActions();
  const [editing, setEditing] = useState<Editing>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);

  const details = listing.category === 'shiur' ? listing.details : undefined;
  const stored = useMemo(() => details?.sessions ?? [], [details?.sessions]);
  const sorted = useMemo(() => sortHoursEntries(stored), [stored]);
  const shulId = details?.where === 'shul' ? details.shulId ?? '' : '';
  const inShul = !!shulId;
  const { listing: shulListing } = useListing(shulId);
  const shul = shulListing?.category === 'shul' ? shulListing : null;

  // Live preview: stored times with the one being edited applied.
  const previewSessions = useMemo(() => {
    if (!editing || checkShiurDraft(editing.draft, inShul).length) return stored;
    const s = draftToSession(editing.draft, editing.id ?? 'preview');
    return editing.id ? stored.map((x) => (x.id === editing.id ? s : x)) : [...stored, s];
  }, [stored, editing, inShul]);
  const week = useShiurWeek(previewSessions, shul);

  const write = async (sessions: ShiurSession[]) => {
    setFailed(false);
    try {
      await updateListing(listing.id, { 'details.sessions': sessions });
      return true;
    } catch {
      setFailed(true);
      return false;
    }
  };

  const saveSession = async () => {
    if (!editing) return;
    const problems = checkShiurDraft(editing.draft, inShul);
    setErrors(problems);
    if (problems.length) return;
    const s = draftToSession(editing.draft, editing.id ?? newId());
    const next = editing.id ? stored.map((x) => (x.id === editing.id ? s : x)) : [...stored, s];
    if (await write(next)) setEditing(null);
  };

  const removeSession = async (id: string) => {
    if (!window.confirm(t('directory.shiur.times.confirmDelete'))) return;
    await write(stored.filter((s) => s.id !== id));
  };

  const startEdit = (next: Editing) => {
    setErrors([]);
    setEditing(next);
  };

  const ruleText = (rule: TimeRule) => {
    if (rule.type === 'fixed') return rule.time;
    const sign = rule.offsetMinutes >= 0 ? '+' : '';
    const zman =
      rule.baseZman === SHABBOS_ENDS_ZMAN
        ? t('directory.admin.hours.shabbosEnds', { min: SHABBOS_ENDS_MINUTES_AFTER_SUNSET })
        : rule.baseZman === CANDLE_LIGHTING_ZMAN
          ? t('zmanim.candleLighting')
          : MYZMANIM_FIELDS[rule.baseZman] || rule.baseZman;
    return `${zman} ${sign}${rule.offsetMinutes} ${t('admin.minutesShort')}`;
  };

  const startText = (s: ShiurSession) => {
    const r = s.start;
    if (r.type !== 'minyan') return ruleText(r) + (s.end ? ` – ${ruleText(s.end)}` : '');
    const when =
      r.anchor === 'betweenMinchaMaariv'
        ? t('directory.shiur.show.betweenMinchaMaariv')
        : t(`directory.shiur.show.${r.anchor}`, { prayer: t(`prayers.${r.prayer ?? 'maariv'}`) });
    const minutes = `${r.anchor === 'before' ? '−' : '+'}${r.offsetMinutes} ${t('admin.minutesShort')}`;
    return `${when} (${minutes}, ${t(`directory.shiur.times.pick.${r.pick}`)})${s.end ? ` – ${ruleText(s.end)}` : ''}`;
  };

  const whenText = (s: ShiurSession) => {
    if (s.specificDate) return s.specificDate;
    const special = s.dayTypes.filter((d) => d !== 'weekday');
    const days = s.daysOfWeek.length || special.length ? s.daysOfWeek : ([0, 1, 2, 3, 4, 5] as const);
    const dayText = dayRuns([...days]).map(([a, b]) =>
      a === b
        ? t(`directory.dayOfWeekShort.${a}`)
        : `${t(`directory.dayOfWeekShort.${a}`)}–${t(`directory.dayOfWeekShort.${b}`)}`
    );
    return [...dayText, ...special.map((d) => t(`dayTypes.${d}`))].join(', ');
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <SectionTitle>{t('directory.shiur.times.title')}</SectionTitle>
        <p className="text-sm text-ash">{t('directory.shiur.times.hint')}</p>
        {sorted.length === 0 && <p className="text-sm text-ink">{t('directory.shiur.times.none')}</p>}
        <ul className="space-y-2">
          {sorted.map((s) =>
            editing?.id === s.id ? (
              <li key={s.id}>
                <SessionForm
                  draft={editing.draft}
                  inShul={inShul}
                  onChange={(draft) => setEditing({ id: s.id, draft })}
                  onSave={saveSession}
                  onCancel={() => startEdit(null)}
                  saving={saving}
                  errors={errors}
                />
              </li>
            ) : (
              <li key={s.id} className="rounded-[16px] border border-ink/10 p-3 flex items-start gap-2">
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium text-ink">{whenText(s)}</p>
                  <p className="text-ink">{s.cancelled ? t('directory.shiur.times.cancelled') : startText(s)}</p>
                  {s.season && <p className="text-xs text-ash">{t(`directory.admin.hours.season.${s.season}`)}</p>}
                  {(s.validFrom || s.validTo) && (
                    <p className="text-xs text-ash">
                      {t('directory.admin.hours.seasonLabel', { from: s.validFrom || '…', to: s.validTo || '…' })}
                    </p>
                  )}
                  {(s.noteHe || s.noteEn) && <p className="text-xs text-ash">{pickLang(s.noteEn, s.noteHe, isHebrew)}</p>}
                </div>
                <div className="flex shrink-0">
                  <IconButton label={t('common.edit')} onClick={() => startEdit({ id: s.id, draft: sessionToDraft(s) })}>
                    <Pencil className="w-4 h-4" aria-hidden />
                  </IconButton>
                  <IconButton
                    label={t('directory.admin.hours.copy')}
                    onClick={() => startEdit({ id: null, draft: sessionToDraft(s) })}
                    disabled={stored.length >= MAX_SESSIONS}
                  >
                    <Copy className="w-4 h-4" aria-hidden />
                  </IconButton>
                  <IconButton label={t('common.delete')} onClick={() => removeSession(s.id)} danger>
                    <Trash2 className="w-4 h-4" aria-hidden />
                  </IconButton>
                </div>
              </li>
            )
          )}
        </ul>

        {editing && editing.id === null ? (
          <SessionForm
            draft={editing.draft}
            inShul={inShul}
            onChange={(draft) => setEditing({ id: null, draft })}
            onSave={saveSession}
            onCancel={() => startEdit(null)}
            saving={saving}
            errors={errors}
          />
        ) : (
          stored.length < MAX_SESSIONS && (
            <button
              type="button"
              onClick={() => startEdit({ id: null, draft: emptyShiurDraft(inShul) })}
              className="inline-flex items-center gap-2 min-h-[44px] px-4 rounded-[10px] border border-ink/20 text-sm text-ink hover:border-ink/50"
            >
              <Plus className="w-4 h-4" aria-hidden />
              {t('directory.shiur.times.add')}
            </button>
          )
        )}
        {failed && <p className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
      </section>

      <section className="space-y-2 pt-2 border-t border-ink/10">
        <SectionTitle>{t('directory.shiur.times.previewTitle')}</SectionTitle>
        <p className="text-xs text-ash">{t('directory.shiur.times.previewHint')}</p>
        <ShiurWeek days={week} />
      </section>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  children,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center w-11 h-11 rounded-[10px] hover:bg-fog disabled:opacity-30 ${
        danger ? 'text-danger-600' : 'text-ink'
      }`}
    >
      {children}
    </button>
  );
}

function PillChoice<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: string;
  options: { value: T; label: string; disabled?: boolean }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-ink mb-1">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            aria-pressed={value === o.value}
            disabled={o.disabled}
            onClick={() => onChange(o.value)}
            className={`min-h-[44px] px-3 rounded-full border text-sm disabled:opacity-40 ${
              value === o.value ? 'bg-ink text-paper border-ink' : 'bg-paper text-ink border-ink/20 hover:border-ink/50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function SessionForm({
  draft,
  inShul,
  onChange,
  onSave,
  onCancel,
  saving,
  errors,
}: {
  draft: ShiurSessionDraft;
  inShul: boolean;
  onChange: (draft: ShiurSessionDraft) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  errors: string[];
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<ShiurSessionDraft>) => onChange({ ...draft, ...patch });
  const extraZmanim = [
    { key: CANDLE_LIGHTING_ZMAN, label: t('zmanim.candleLighting') },
    { key: SHABBOS_ENDS_ZMAN, label: t('directory.admin.hours.shabbosEnds', { min: SHABBOS_ENDS_MINUTES_AFTER_SUNSET }) },
  ];
  const between = draft.startMode === 'minyan' && draft.anchor === 'betweenMinchaMaariv';
  const offsetLabel = between
    ? t('directory.shiur.times.offsetBetween')
    : draft.anchor === 'before'
      ? t('directory.shiur.times.offsetBefore')
      : t('directory.shiur.times.offsetAfter');

  return (
    <div className="bg-fog rounded-[16px] border border-ink/10 p-4 space-y-4">
      <PillChoice
        legend={t('directory.shiur.times.kindTitle')}
        options={(['days', 'date'] as const).map((k) => ({ value: k, label: t(`directory.admin.hours.kind.${k}`) }))}
        value={draft.kind}
        onChange={(kind) => set({ kind })}
      />

      {draft.kind === 'days' && (
        <>
          <ChipSelect
            label={t('directory.field.daysOfWeek')}
            options={DAYS_OF_WEEK.map((d) => ({ value: d, label: t(`directory.dayOfWeekShort.${d}`) }))}
            selected={draft.daysOfWeek}
            onChange={(daysOfWeek) => set({ daysOfWeek })}
          />
          <ChipSelect<HoursDayType>
            label={t('directory.field.dayTypes')}
            info={t('directory.shiur.times.specialHint')}
            options={SHIUR_SPECIAL_DAY_TYPES.map((d) => ({ value: d, label: t(`dayTypes.${d}`) }))}
            selected={draft.dayTypes}
            onChange={(dayTypes) => set({ dayTypes })}
          />
        </>
      )}
      {draft.kind === 'date' && (
        <>
          <div>
            <label htmlFor="shiur-date" className="block text-sm font-medium text-ink mb-1">
              {t('directory.field.specificDate')}
            </label>
            <input
              id="shiur-date"
              type="date"
              value={draft.specificDate}
              onChange={(e) => set({ specificDate: e.target.value })}
              className="min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
            />
          </div>
          <Checkbox label={t('directory.shiur.times.cancelled')} checked={draft.cancelled} onChange={(cancelled) => set({ cancelled })} />
        </>
      )}

      {!(draft.kind === 'date' && draft.cancelled) && (
        <>
          <div className="space-y-3 rounded-[10px] bg-paper border border-ink/10 p-3">
            <PillChoice
              legend={t('directory.shiur.times.startTitle')}
              options={[
                { value: 'time' as const, label: t('directory.shiur.times.startMode.time') },
                { value: 'minyan' as const, label: t('directory.shiur.times.startMode.minyan'), disabled: !inShul },
              ]}
              value={draft.startMode}
              onChange={(startMode) => set({ startMode })}
            />
            {!inShul && <p className="text-xs text-ash">{t('directory.shiur.times.minyanOnlyInShul')}</p>}

            {draft.startMode === 'time' ? (
              <TimeRuleFields
                value={draft.start}
                onChange={(patch) => set({ start: { ...draft.start, ...patch } })}
                idPrefix="shiur-start"
                radioName="shiur-start-type"
                fixedTimeLabel={t('directory.shiur.times.startTitle')}
                extraZmanim={extraZmanim}
              />
            ) : (
              <>
                <PillChoice
                  legend={t('directory.shiur.times.anchorTitle')}
                  options={SHIUR_ANCHORS.map((a) => ({ value: a, label: t(`directory.shiur.times.anchor.${a}`) }))}
                  value={draft.anchor}
                  onChange={(anchor) =>
                    set({ anchor, ...(anchor === 'betweenMinchaMaariv' ? { offsetMinutes: draft.offsetMinutes || 15 } : {}) })
                  }
                />
                {!between && (
                  <SelectField
                    label={t('directory.shiur.times.prayer')}
                    value={draft.prayer}
                    onChange={(prayer) => set({ prayer })}
                    options={SHIUR_ANCHOR_PRAYERS.map((p) => ({ value: p, label: t(`prayers.${p}`) }))}
                  />
                )}
                <TextField
                  label={offsetLabel}
                  type="number"
                  inputMode="numeric"
                  dir="ltr"
                  value={String(draft.offsetMinutes)}
                  onChange={(v) => set({ offsetMinutes: v === '' ? NaN : Number(v) })}
                />
                <SelectField
                  label={t('directory.shiur.times.pickTitle')}
                  value={draft.pick}
                  onChange={(pick) => set({ pick })}
                  options={SHIUR_ANCHOR_PICKS.map((p) => ({ value: p, label: t(`directory.shiur.times.pick.${p}`) }))}
                />
                {between && <p className="text-xs text-ash">{t('directory.shiur.times.betweenHint')}</p>}
              </>
            )}
          </div>

          {!between && (
            <div className="space-y-3 rounded-[10px] bg-paper border border-ink/10 p-3">
              <Checkbox label={t('directory.shiur.times.hasEnd')} checked={draft.hasEnd} onChange={(hasEnd) => set({ hasEnd })} />
              {draft.hasEnd && (
                <TimeRuleFields
                  value={draft.end}
                  onChange={(patch) => set({ end: { ...draft.end, ...patch } })}
                  idPrefix="shiur-end"
                  radioName="shiur-end-type"
                  fixedTimeLabel={t('directory.shiur.times.endTitle')}
                  extraZmanim={extraZmanim}
                />
              )}
            </div>
          )}
        </>
      )}

      <BilingualField
        labelHe={t('directory.field.noteHe')}
        labelEn={t('directory.field.noteEn')}
        valueHe={draft.noteHe}
        valueEn={draft.noteEn}
        onChangeHe={(noteHe) => set({ noteHe })}
        onChangeEn={(noteEn) => set({ noteEn })}
        maxLength={LISTING_LIMITS.noteLength}
      />

      {draft.kind !== 'date' && (
        <>
          <SelectField<HoursSeasonMode>
            label={t('directory.admin.hours.seasonTitle')}
            info={t('directory.admin.hours.seasonHint')}
            value={draft.season}
            onChange={(season) => set({ season })}
            options={HOURS_SEASON_MODES.map((m) => ({ value: m, label: t(`directory.admin.hours.season.${m}`) }))}
          />
          {draft.season === 'dates' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="shiur-from" className="block text-sm font-medium text-ink mb-1">
                  {t('directory.field.validFrom')}
                </label>
                <input
                  id="shiur-from"
                  type="date"
                  value={draft.validFrom}
                  onChange={(e) => set({ validFrom: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
                />
              </div>
              <div>
                <label htmlFor="shiur-to" className="block text-sm font-medium text-ink mb-1">
                  {t('directory.field.validTo')}
                </label>
                <input
                  id="shiur-to"
                  type="date"
                  value={draft.validTo}
                  onChange={(e) => set({ validTo: e.target.value })}
                  className="w-full min-h-[44px] px-3 py-2 rounded-[10px] border border-ink bg-paper text-ink text-sm"
                />
              </div>
            </div>
          )}
        </>
      )}

      <ErrorList errors={errors} />
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="min-h-[44px] px-5 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 disabled:opacity-40"
        >
          {saving ? t('directory.admin.saving') : t('directory.admin.save')}
        </button>
        <button type="button" onClick={onCancel} className="min-h-[44px] px-4 rounded-[10px] text-sm text-ink hover:opacity-70">
          {t('directory.admin.cancel')}
        </button>
      </div>
    </div>
  );
}
